import React, { useMemo, useState } from 'react';
import {
    Combobox,
    ComboboxInput,
    ComboboxOption,
    ComboboxOptions,
} from '@headlessui/react';
import { twMerge } from 'tailwind-merge';
import { useAppSelector } from 'flow/hooks/useRedux';
import { TagKey } from 'flow/types/EntityKeys';
import { Tag } from 'flow/types/Tag';
import { TypedMap } from 'flow/types/TypedMap';
import { selectAllTagsAvailableForTicketTagging } from 'flow/selectors/TicketsSelectors';
import useAppContext from 'flow/hooks/useAppContext';
import TagRemovable from 'flow/components/custom-components/TagRemovable';
import { useFilterPopupHeight } from './useFilterPopupHeight';

interface Props {
    /** Currently-selected tag keys. Empty array = no tag filter. */
    value: TagKey[];
    onChange: (next: TagKey[]) => void;
    disabled?: boolean;
}

/**
 * Multi-select tag filter field. Selected tags render as
 * `TagRemovable` pills inside the field (matching the details-panel
 * `TicketTagsList` UX), with a typing area on the right that filters
 * the dropdown. Clicking a dropdown entry adds the tag and clears
 * the query; clicking a pill's X removes that tag.
 *
 * OR semantics: a ticket matches if it carries any one of the
 * selected tags. See `TicketsListSearchResultWrapper` for the filter
 * implementation.
 */
const TagPicker = ({ value, onChange, disabled }: Props) => {
    const { i18next } = useAppContext();
    const allTags = useAppSelector(selectAllTagsAvailableForTicketTagging);
    const [query, setQuery] = useState('');

    // Flatten the Immutable Map → array of { key, label } for
    // straightforward filtering / rendering. Sorted alphabetically
    // by label so the dropdown reads predictably.
    const tagList = useMemo(() => {
        const arr: { key: TagKey; label: string }[] = [];
        allTags?.forEach?.((tag: TypedMap<Tag>) => {
            arr.push({
                key: tag.get('key'),
                label: tag.get('label') ?? '',
            });
        });
        arr.sort((a, b) => a.label.localeCompare(b.label));
        return arr;
    }, [allTags]);

    // Filter the dropdown by the user's query, AND hide tags that are
    // already selected (so the dropdown only shows what's left to add).
    // Returns keys only — labels are resolved at render time via
    // `labelFor`, so the array is cheap to pass into Headless UI's
    // virtual mode.
    const visibleTagKeys = useMemo<TagKey[]>(() => {
        const q = query.trim().toLowerCase();
        const selectedSet = new Set(value);
        return tagList
            .filter((t) => {
                if (selectedSet.has(t.key)) return false;
                if (!q) return true;
                return t.label.toLowerCase().includes(q);
            })
            .map((t) => t.key);
    }, [tagList, value, query]);

    // Label lookup for rendering selected pills (the parent only
    // hands us keys, not labels).
    const labelFor = (key: TagKey): string =>
        tagList.find((t) => t.key === key)?.label ?? key;

    // Cap dropdown height to the filter popup's height.
    const { wrapperRef, popupHeight } = useFilterPopupHeight();

    const removeTag = (key: TagKey) => {
        onChange(value.filter((k) => k !== key));
    };

    const resolvePlaceholder = () => {
        if (value.length > 0) return '';
        if (disabled) return i18next.t('tickets.filter.no_tags_available');
        return i18next.t('tickets.filter.tag_placeholder');
    };

    return (
        <Combobox
            disabled={disabled}
            // `multiple` makes Combobox manage an array of values; we
            // hand the current selection in and update on each change.
            multiple={true}
            value={value}
            onChange={(next: TagKey[]) => {
                onChange(next);
                setQuery('');
            }}
            onClose={() => setQuery('')}
            immediate={true}
            // Headless UI's built-in virtualisation — only the rows
            // visible in the dropdown viewport render. Skipped when
            // the list is empty so the no-matches placeholder below
            // can render as a static child.
            virtual={
                visibleTagKeys.length > 0
                    ? { options: visibleTagKeys }
                    : undefined
            }
        >
            <div ref={wrapperRef} className="relative">
                {/* Pill row + input share a single backplate so the
                 *  whole thing reads as one field. `flex-wrap` lets
                 *  multiple pills overflow to a second line; the
                 *  input flexes to take the remaining space.
                 *  `min-h-9` (36px) enforces the same default height
                 *  as the sibling fields when no pill is selected —
                 *  pills (h-6) + py-1.5 also sum to 36px, so the
                 *  field height stays stable across states. */}
                <div className="flex min-h-9 w-full flex-wrap items-center gap-1 rounded-sm bg-gray-200 py-1.5 ps-2 pe-3 text-sm dark:bg-gray-100">
                    {value.map((tagKey) => (
                        <TagRemovable
                            key={tagKey}
                            tagKey={tagKey}
                            text={labelFor(tagKey)}
                            onClickRemove={(event, key) => {
                                // Stop the click from bubbling to the
                                // ComboboxInput / Combobox root and
                                // re-opening the dropdown.
                                event.stopPropagation();
                                removeTag(key as TagKey);
                            }}
                        />
                    ))}
                    <ComboboxInput
                        className="min-w-[6rem] flex-1 bg-transparent text-sm text-gray-800 placeholder:text-gray-500 focus:outline-hidden"
                        autoComplete="off"
                        placeholder={resolvePlaceholder()}
                        // The input shows the in-progress query string
                        // — the selection is rendered as pills above.
                        displayValue={() => query}
                        onChange={(e) => setQuery(e.target.value)}
                        onKeyDown={(e) => {
                            // Backspace on an empty query removes the
                            // last pill — matches the standard
                            // chip-input affordance (Gmail, Slack…).
                            if (
                                e.key === 'Backspace' &&
                                query === '' &&
                                value.length > 0
                            ) {
                                e.preventDefault();
                                removeTag(value[value.length - 1]);
                            }
                        }}
                    />
                </div>
                {/* No chevron or clear-all button — each pill carries
                 *  its own X (via `TagRemovable`), and the dropdown
                 *  opens on input focus via `immediate`. */}
                <ComboboxOptions
                    anchor="bottom start"
                    // `modal={false}` opts out of Headless UI's
                    // default focus-trap behaviour for the open
                    // panel — without it, every other element on
                    // the page receives `inert` + `aria-hidden`,
                    // and the accompanying scroll-lock pads the
                    // body, which shifts the main content pane
                    // sideways while the dropdown is open.
                    modal={false}
                    style={
                        popupHeight
                            ? ({
                                  '--anchor-max-height': `${popupHeight}px`,
                              } as React.CSSProperties)
                            : undefined
                    }
                    className="scrollbar shadow-base scrollbar-gutter-stable z-50 mt-1 w-(--input-width) origin-top overflow-auto rounded-lg bg-white py-2 ps-2 outline-gray-100 focus:outline dark:bg-gray-300"
                >
                    {visibleTagKeys.length === 0 ? (
                        <div className="px-3 py-2 text-sm text-gray-500">
                            {i18next.t('tickets.filter.no_tags')}
                        </div>
                    ) : (
                        ({ option }) => (
                            <ComboboxOption
                                value={option}
                                className={({ focus }) =>
                                    twMerge(
                                        'flex w-full cursor-pointer items-center rounded-sm py-1.5 ps-2 text-sm text-gray-800',
                                        focus && 'bg-gray-200 dark:bg-gray-400',
                                    )
                                }
                            >
                                <span className="truncate">
                                    {labelFor(option)}
                                </span>
                            </ComboboxOption>
                        )
                    )}
                </ComboboxOptions>
            </div>
        </Combobox>
    );
};

export default TagPicker;
